import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { db } from "../../firebase";
import { t } from "i18next";

const PAGE_SIZE = 18;

const ImageGallery = ({ fleetId, type, transportOrderId, userId, titleClassName = "" }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [fetchLimit, setFetchLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    setFetchLimit(PAGE_SIZE);
  }, [fleetId, type, transportOrderId, userId]);

  useEffect(() => {
    if (!fleetId || (!userId && !transportOrderId)) {
      setImages([]);
      setLoading(false);
      return undefined;
    }

    let galleryQuery;
    if (type === "driver" && userId) {
      galleryQuery = query(
        collection(db, "fleets", fleetId, "images"),
        where("userId", "==", userId),
        orderBy("timestamp", "desc"),
        limit(fetchLimit)
      );
    } else if (type === "transportOrder" && transportOrderId) {
      galleryQuery = query(
        collection(db, "fleets", fleetId, "images"),
        where("transportOrderId", "==", transportOrderId),
        orderBy("timestamp", "desc"),
        limit(fetchLimit)
      );
    } else {
      setError(
        t("imageGallery.invalidParameters", {
          defaultValue: "Invalid parameters for image gallery.",
        })
      );
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const unsubscribe = onSnapshot(
      galleryQuery,
      (snapshot) => {
        const nextImages = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));
        setImages(nextImages);
        setHasMore(nextImages.length >= fetchLimit);
        setLoading(false);
      },
      (snapshotError) => {
        console.error("Error subscribing to gallery images:", snapshotError);
        setError(
          t("imageGallery.loadFailed", {
            defaultValue: "Failed to load images.",
          })
        );
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [fleetId, userId, transportOrderId, type, fetchLimit]);

  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setFetchLimit((previous) => previous + PAGE_SIZE);
        }
      },
      { rootMargin: "220px" }
    );

    const target = loadMoreRef.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [hasMore, loading]);

  if (loading) return <div>{t("common.loading", { defaultValue: "Loading..." })}</div>;
  if (error) return <div>{error}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <h1
        className={titleClassName}
        style={titleClassName ? { paddingBottom: "15px", margin: 0 } : {
          color: "var(--chakra-colors-gandolapp-secondary)",
          fontSize: "16px",
          fontWeight: "bold",
          paddingBottom: "15px",
        }}
      >
        {t("orderDetail.photoGallery")}
      </h1>

      {images.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "15px",
            justifyItems: "center",
          }}
        >
          {images.map((image) => (
            <div
              key={image.id}
              style={{ maxWidth: "200px", cursor: "pointer" }}
              onClick={() => setSelectedImage(image.url)}
            >
              <img
                src={image.url}
                alt={image.description || t("imageGallery.imageAlt", { defaultValue: "Image" })}
                style={{ borderRadius: "5px", width: "100%" }}
              />
            </div>
          ))}
          {hasMore && <div ref={loadMoreRef} style={{ height: 1, width: "100%" }} />}
        </div>
      ) : (
        <div>{t("orderDetail.noImagesFound")}</div>
      )}

      {selectedImage &&
        createPortal(
          <div
            onClick={() => setSelectedImage(null)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <img
              src={selectedImage}
              alt={t("imageGallery.expandedImageAlt", { defaultValue: "Expanded image" })}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                borderRadius: "10px",
                boxShadow: "0 0 20px #000",
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ImageGallery;
